import React, { useEffect } from "react"
import { Card, Button, IconButton } from 'components/common';
import { FaArrowLeft } from 'react-icons/fa';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { createApplication, updateApplication } from 'redux/integration/applicationSlice';
import PropTypes from "prop-types";

import { INPUT_CLS as INPUT_CLASSES } from 'helpers/formStyles';
const ApplicationForm = ({application, title}) => {
  const {register, handleSubmit, formState: {errors}, setValue } = useForm();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if(application) {
      Object.entries(application).forEach(([key, value]) => {
        setValue(key, value)
      })
    }
  }, [setValue, application]);

  const onSubmit = async (data) => {
    try {
      let applicationId = null

      if (application) {
        applicationId = application.id
        dispatch(updateApplication({applicationId, data }))
      }
      else {
        const response = await dispatch(createApplication(data))

        if (response.payload && response.payload?.data?.id) {
          applicationId = response.payload.data.id;
        } else {
          toast.error(response.payload.error)
        }
      }

      if (applicationId) {
        navigate(`/integration/applications/${applicationId}`)
      }
    }
    catch(error) {
      console.error('Application submit failed:', error)
      toast.error(error.message)

      return;
    }
  }

  const handleBack = () => {
    navigate(-1);
  }

  return (
    <Card
      className="tw-text-sm"
      padding="none"
      header={(
        <div className="tw-flex tw-items-center tw-justify-between">
          <h3 className="tw-font-semibold tw-text-sm tw-text-slate-900 tw-mb-0">{title}</h3>
          <IconButton
            onClick={handleBack}
            variant="ghost"
            size="sm"
            icon={<FaArrowLeft />}
          />
        </div>
      )}
    >
      <form id="application-form" onSubmit={handleSubmit(onSubmit)} className="tw-text-sm tw-p-4">
        <div className="tw-mb-2 tw-text-blue-600">Application for authorization to integrate with another system.</div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-2 tw-mt-6 tw-items-start">
          <label htmlFor="name" className="tw-col-span-12 sm:tw-col-span-3 lg:tw-text-right tw-pt-2">
            Application name
          </label>
          <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
            <input
              id="name"
              className={INPUT_CLASSES}
              placeholder="Application name"
              {...register("name", {
                required: "Application name must be required."
              })}
            />
            <span className="tw-text-red-600">
              {errors.name && errors.name.message}
            </span>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-2 tw-items-start">
          <label htmlFor="redirect_uri" className="tw-col-span-12 sm:tw-col-span-3 lg:tw-text-right tw-pt-2">
            Redirect url
          </label>
          <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
            <input
              id="redirect_uri"
              className={INPUT_CLASSES}
              placeholder="Redirect url"
              {...register("redirect_uri")}
            />
            <span className="tw-text-red-600">
              {errors.redirect_uri && errors.redirect_uri.message}
            </span>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-2 tw-items-start">
          <label htmlFor="scopes" className="tw-col-span-12 sm:tw-col-span-3 lg:tw-text-right tw-pt-2">
            Scopes
          </label>
          <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
            <input
              id="scopes"
              className={INPUT_CLASSES}
              placeholder="Scopes"
              {...register("scopes")}
            />
            <div className="tw-text-blue-600 tw-my-1 tw-text-right">
              Optional scopes: write, update, public (default).
            </div>
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-4 tw-mb-2 tw-mt-4">
          <div className="tw-col-span-12 sm:tw-col-span-3" />
          <div className="tw-col-span-12 sm:tw-col-span-9 md:tw-col-span-7">
            <label htmlFor="confidential" className="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer">
              <input
                id="confidential"
                type="checkbox"
                className="tw-h-4 tw-w-4 tw-rounded-sm tw-border-slate-300"
                {...register("confidential")}
              />
              Confidential
            </label>
          </div>
        </div>

        <div className="tw-fixed tw-bottom-0 tw-inset-x-0 tw-bg-slate-100 tw-text-center tw-p-3 tw-z-10">
          <Button size="sm" type="submit" variant="primary" form="application-form" className="tw-mr-2">
            {application ? "Update" : "Create"} Application
          </Button>
          <Button size="sm" variant="danger" onClick={handleBack}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
};

ApplicationForm.propTypes = {
  application: PropTypes.shape({
    id: PropTypes.number,
  }),
  title: PropTypes.string.isRequired,
};

export default ApplicationForm;
